/* OTERVANO V23.11.2 — Fit-first detail viewer. No cart/variant requests; only source media is read. */
(() => {
  'use strict';
  if (window.OvProductDetailViewer) return;
  const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
  class OvProductDetailViewer {
    constructor(modal) {
      this.modal = modal;
      this.shell = modal.querySelector('[data-ovz-shell]');
      this.stage = modal.querySelector('[data-ovz-stage]');
      this.image = modal.querySelector('[data-ovz-image]');
      this.loading = modal.querySelector('[data-ovz-loading]');
      this.count = modal.querySelector('[data-ovz-count]');
      this.level = modal.querySelector('[data-ovz-level]');
      this.announcement = modal.querySelector('[data-ovz-announcement]');
      this.prev = modal.querySelector('[data-ovz-prev]');
      this.next = modal.querySelector('[data-ovz-next]');
      this.minus = modal.querySelector('[data-ovz-minus]');
      this.plus = modal.querySelector('[data-ovz-plus]');
      this.fitButton = modal.querySelector('[data-ovz-fit]');
      this.max = clamp(Number(modal.dataset.zoomMax) || 3, 2, 4);
      this.scale = 1; this.x = 0; this.y = 0;
      this.pointers = new Map();
    }
    open(opener) {
      const id = opener?.getAttribute('data-media-id');
      // Native ProductInfo can replace this list after selecting another color. Read it on every opening.
      this.sources = [...this.modal.querySelectorAll('.product-media-modal__content > img[data-media-id]')];
      const index = this.sources.findIndex(source => source.dataset.mediaId === id);
      if (index < 0 || !this.shell || !this.stage || !this.image) return false;
      this.close(false);
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      if (typeof removeTrapFocus === 'function') removeTrapFocus();
      this.modal.setAttribute('data-ov-photo-active', '');
      this.shell.hidden = false;
      this.prev.addEventListener('click', () => this.change(-1), options);
      this.next.addEventListener('click', () => this.change(1), options);
      this.minus.addEventListener('click', () => this.zoom(this.scale - 0.5), options);
      this.plus.addEventListener('click', () => this.zoom(this.scale + 0.5), options);
      this.fitButton.addEventListener('click', () => this.reset(), options);
      this.stage.addEventListener('pointerdown', e => this.pointerDown(e), options);
      this.stage.addEventListener('pointermove', e => this.pointerMove(e), options);
      this.stage.addEventListener('pointerup', e => this.pointerUp(e), options);
      this.stage.addEventListener('pointercancel', e => this.pointerUp(e, true), options);
      this.stage.addEventListener('lostpointercapture', e => this.pointerUp(e, true), options);
      this.stage.addEventListener('wheel', e => {
        e.preventDefault();
        this.zoom(this.scale * (e.deltaY > 0 ? 0.9 : 1.1), this.local(e));
      }, { ...options, passive: false });
      this.modal.addEventListener('keydown', e => {
        if (!this.controller || e.altKey || e.metaKey || e.ctrlKey) return;
        if (e.key === 'Tab') {
          // The native trap was built before the viewer appeared. Use current, visible controls instead.
          const focusable = [...this.modal.querySelectorAll('button:not(:disabled),[tabindex="0"]')].filter(el => el.getClientRects().length && !el.closest('[hidden]'));
          const first = focusable[0], last = focusable.at(-1);
          e.stopImmediatePropagation();
          if (!first) return;
          if (e.shiftKey && (document.activeElement === first || !focusable.includes(document.activeElement))) { e.preventDefault(); last.focus(); }
          else if (!e.shiftKey && (document.activeElement === last || !focusable.includes(document.activeElement))) { e.preventDefault(); first.focus(); }
          return;
        }
        if (e.key === '+' || e.key === '=') { e.preventDefault(); this.zoom(this.scale + 0.5); }
        else if (e.key === '-') { e.preventDefault(); this.zoom(this.scale - 0.5); }
        else if (e.key === '0') { e.preventDefault(); this.reset(); }
        else if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
          e.preventDefault(); this.change(e.key === 'ArrowRight' ? 1 : -1);
        }
        // Escape and Tab are handled by the theme's original ModalDialog/focus trap.
      }, options);
      this.image.addEventListener('load', () => this.imageReady(), options);
      this.image.addEventListener('error', () => {
        if (!this.controller) return;
        this.ready = false; this.image.hidden = true;
        this.loading.hidden = false;
        this.loading.textContent = 'No se pudo cargar esta foto. Prueba otra imagen o cierra el visor.';
        this.paint();
      }, options);
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => {
          if (this.controller && this.ready) this.measure(true);
        });
        this.resizeObserver.observe(this.stage);
      }
      this.setMedia(index);
      this.modal.querySelector('[id^="ModalClose-"]')?.focus({ preventScroll: true });
      return true;
    }
    close(clearMedia = true) {
      this.controller?.abort(); this.controller = null;
      this.resizeObserver?.disconnect();
      this.resizeObserver = null;
      this.pointers.clear(); this.gesture = null; this.tap = null;
      this.stage?.removeAttribute('data-dragging');
      this.modal.removeAttribute('data-ov-photo-active');
      if (this.shell) this.shell.hidden = true;
      if (clearMedia && this.image) {
        this.image.removeAttribute('srcset'); this.image.removeAttribute('src'); this.image.hidden = true;
      }
    }
    setMedia(index) {
      this.index = clamp(index, 0, this.sources.length - 1);
      const source = this.sources[this.index];
      this.ready = false;
      this.pointers.clear(); this.gesture = null; this.tap = null;
      this.scale = 1; this.x = this.y = 0;
      this.image.hidden = true;
      this.stage.removeAttribute('data-dragging');
      this.loading.textContent = 'Cargando foto…'; this.loading.hidden = false;
      this.count.textContent = `${this.index + 1} / ${this.sources.length}`;
      this.image.alt = source.alt || this.modal.querySelector('.ovz-name')?.textContent?.trim() || 'Detalle del producto';
      this.image.sizes = `${Math.max(200, Math.ceil(this.stage.clientWidth))}px`;
      const srcset = source.getAttribute('srcset');
      if (srcset) this.image.setAttribute('srcset', srcset); else this.image.removeAttribute('srcset');
      this.image.src = source.getAttribute('src') || source.currentSrc;
      this.paint();
      if (this.image.complete && this.image.naturalWidth > 0) this.imageReady();
      this.announcement.textContent = `Foto ${this.index + 1} de ${this.sources.length}.`;
    }
    imageReady() {
      if (!this.controller || !this.image.naturalWidth) return;
      this.ready = true;
      this.loading.hidden = true;
      this.image.hidden = false;
      this.measure(false);
    }
    measure(reset) {
      const width = this.stage.clientWidth, height = this.stage.clientHeight;
      if (!width || !height) return;
      const ratio = this.image.naturalWidth / this.image.naturalHeight;
      // Never force an 1100px-wide picture into a phone. Start contained in the available viewport.
      this.baseWidth = Math.max(1, Math.min(width - 32, (height - 24) * ratio, this.image.naturalWidth));
      this.baseHeight = this.baseWidth / ratio;
      this.image.style.width = `${this.baseWidth}px`;
      this.image.style.height = `${this.baseHeight}px`;
      if (reset) { this.scale = 1; this.x = this.y = 0; }
      this.bound(); this.paint();
    }
    local(event) {
      const rect = this.stage.getBoundingClientRect();
      return { x: event.clientX - rect.left - rect.width / 2, y: event.clientY - rect.top - rect.height / 2 };
    }
    zoom(requested, anchor = { x: 0, y: 0 }) {
      if (!this.ready) return;
      const scale = clamp(requested, 1, this.max);
      const ratio = scale / this.scale;
      this.x = anchor.x - (anchor.x - this.x) * ratio;
      this.y = anchor.y - (anchor.y - this.y) * ratio;
      this.scale = scale;
      this.bound(); this.paint();
      // Let the browser select a larger Shopify CDN source only after the user zooms in.
      this.image.sizes = `${Math.ceil(this.baseWidth * this.scale)}px`;
    }
    reset() { this.scale = 1; this.x = this.y = 0; this.paint(); }
    bound() {
      const mx = Math.max(0, ((this.baseWidth || 0) * this.scale - this.stage.clientWidth) / 2);
      const my = Math.max(0, ((this.baseHeight || 0) * this.scale - this.stage.clientHeight) / 2);
      this.x = clamp(this.x, -mx, mx); this.y = clamp(this.y, -my, my);
      if (this.scale <= 1.001) this.x = this.y = 0;
    }
    paint() {
      if (!this.image) return;
      this.bound();
      this.image.style.transform = `translate(${this.x.toFixed(2)}px,${this.y.toFixed(2)}px) scale(${this.scale.toFixed(4)})`;
      this.stage.toggleAttribute('data-zoomed', this.scale > 1.01);
      this.level.textContent = `${Number(this.scale.toFixed(1))}×`;
      this.minus.disabled = !this.ready || this.scale <= 1.01;
      this.plus.disabled = !this.ready || this.scale >= this.max - 0.01;
      this.fitButton.disabled = !this.ready;
      this.prev.disabled = !this.sources?.length || this.index <= 0;
      this.next.disabled = !this.sources?.length || this.index >= this.sources.length - 1;
    }
    change(direction) {
      const next = this.index + direction;
      if (next >= 0 && next < this.sources.length) this.setMedia(next);
    }
    rebase() {
      const points = [...this.pointers.values()];
      if (points.length >= 2) {
        this.gesture = { type: 'pinch', distance: Math.max(1, Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y)),
          mid: { x: (points[0].x + points[1].x) / 2, y: (points[0].y + points[1].y) / 2 },
          scale: this.scale, x: this.x, y: this.y };
      } else if (points.length) this.gesture = { type: 'pan', start: points[0], x: this.x, y: this.y };
      else this.gesture = null;
    }
    pointerDown(event) {
      if (!this.ready || (event.pointerType === 'mouse' && event.button !== 0)) return;
      event.preventDefault();
      const point = this.local(event);
      this.pointers.set(event.pointerId, point);
      try { this.stage.setPointerCapture(event.pointerId); } catch (_) { /* Older browsers: buttons remain available. */ }
      if (this.pointers.size === 1) {
        this.start = { ...point, time: performance.now(), scale: this.scale };
        this.usedPinch = false;
      } else this.usedPinch = true;
      this.stage.toggleAttribute('data-dragging', this.scale > 1);
      this.rebase();
    }
    pointerMove(event) {
      if (!this.pointers.has(event.pointerId) || !this.gesture) return;
      event.preventDefault();
      this.pointers.set(event.pointerId, this.local(event));
      const points = [...this.pointers.values()], g = this.gesture;
      if (points.length >= 2 && g.type === 'pinch') {
        const distance = Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y);
        const mid = { x: (points[0].x + points[1].x) / 2, y: (points[0].y + points[1].y) / 2 };
        this.scale = clamp(g.scale * distance / g.distance, 1, this.max);
        const ratio = this.scale / g.scale;
        this.x = mid.x - (g.mid.x - g.x) * ratio;
        this.y = mid.y - (g.mid.y - g.y) * ratio;
      } else if (g.type === 'pan' && this.scale > 1) {
        this.x = g.x + points[0].x - g.start.x; this.y = g.y + points[0].y - g.start.y;
      }
      this.bound(); this.paint();
    }
    pointerUp(event, cancelled = false) {
      if (!this.pointers.has(event.pointerId)) return;
      const point = this.local(event);
      const finalPointer = this.pointers.size === 1;
      this.pointers.delete(event.pointerId);
      try { if (this.stage.hasPointerCapture(event.pointerId)) this.stage.releasePointerCapture(event.pointerId); } catch (_) {}
      if (finalPointer && !cancelled && !this.usedPinch && this.start) {
        const dx = point.x - this.start.x, dy = point.y - this.start.y;
        const elapsed = performance.now() - this.start.time;
        if (Math.abs(dx) > 55 && Math.abs(dx) > Math.abs(dy) * 1.4 && this.start.scale <= 1.01) this.change(dx < 0 ? 1 : -1);
        else if (Math.hypot(dx, dy) < 10 && elapsed < 350) {
          const now = performance.now();
          if (this.tap && now - this.tap.time < 350 && Math.hypot(point.x - this.tap.x, point.y - this.tap.y) < 30) {
            this.zoom(this.scale > 1.01 ? 1 : Math.min(2, this.max), point); this.tap = null;
          } else this.tap = { ...point, time: now };
        }
      }
      if (finalPointer) {
        this.stage.removeAttribute('data-dragging');
        this.image.sizes = `${Math.ceil((this.baseWidth || this.stage.clientWidth) * this.scale)}px`;
      }
      this.rebase();
    }
  }
  window.OvProductDetailViewer = OvProductDetailViewer;
})();
